import { AxiosInstance } from 'axios';
import * as Sentry from '@sentry/react';
import { config } from 'config';
import { AxiosClient, AxiosUtils } from '../AxiosClient/AxiosClient';
import { formatDateReverse } from 'utils/date';
import { VehicleMatch, VehicleType } from 'types';
import { SERVICE_DOWN } from 'api/errors';

type ModelVersionsDataType = {
  versionId: string;
  referential: { make: string; model: string; category: string };
  label: string;
  specs: {
    energy: string;
    nbOfDoors: number;
    gearbox: string;
    trimLevel: string | undefined;
    fiscalHorsePower: number;
    cubic: number;
  };
};

// VP Particulier
// VU Utilitaire
// VL Loisir
const getRegistrationType = (vehicleType: VehicleType): string => {
  switch (vehicleType) {
    case 'AUTO':
    case 'MOTO':
      return 'VP';
    case 'UTILITAIRE':
      return 'VU';
    default:
      return 'VL';
  }
};

class VehicleReferential {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = AxiosClient.get({
      baseURL: config.URL_VEHICLE_REFERENTIAL,
      headers: {
        'x-client-source': 'classified:lcab:depot-web',
      },
    });
  }

  async fetchMakes(vehicleType: VehicleType, firstCirculationDate: Date): Promise<string[]> {
    return this.client
      .get('v2/makes', {
        params: {
          registrationTypes: getRegistrationType(vehicleType),
          mecDate: formatDateReverse(firstCirculationDate),
          ...((vehicleType === 'AUTO' || vehicleType === 'MOTO') && { family: vehicleType }),
        },
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(result => {
        const data: any = result.data;
        const makes = data.hits.content.map((hit: { value: string; count: number }) => hit.value);
        return makes;
      })
      .catch(r => {
        Sentry.captureException(r, {
          tags: {
            api: 'vehicle-referential',
            endpoint: 'v2/makes',
            ...AxiosUtils.enrichTags(r),
          },
        });
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  async fetchModels(vehicleType: VehicleType, firstCirculationDate: Date, make: string): Promise<string[]> {
    return this.client
      .get(`v2/makes/${encodeURIComponent(make)}/models`, {
        params: {
          registrationTypes: getRegistrationType(vehicleType),
          mecDate: formatDateReverse(firstCirculationDate),
          ...((vehicleType === 'AUTO' || vehicleType === 'MOTO') && { family: vehicleType }),
        },
        headers: AxiosClient.getAuthorizationHeader(),
      })
      .then(result => {
        const data: any = result.data;
        const models = data.hits.content.map((hit: { value: string; count: number }) => hit.value);
        return models;
      })
      .catch(r => {
        Sentry.captureException(r, {
          tags: {
            api: 'vehicle-referential',
            endpoint: `v2/makes/${make}/models`,
            ...AxiosUtils.enrichTags(r),
          },
        });
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }

  async fetchVersions(
    vehicleType: VehicleType,
    firstCirculationDate: Date,
    make: string,
    model: string,
  ): Promise<VehicleMatch[]> {
    return this.client
      .get(`v2/makes/${encodeURIComponent(make)}/models/${encodeURIComponent(model)}/versions`, {
        params: {
          registrationTypes: getRegistrationType(vehicleType),
          mecDate: formatDateReverse(firstCirculationDate),
          ...((vehicleType === 'AUTO' || vehicleType === 'MOTO') && { family: vehicleType }),
        },
        headers: {
          ...AxiosClient.getAuthorizationHeader(),
          accept: 'application/medium+json',
        },
      })
      .then(result => {
        const data: any = result.data;
        const versions: VehicleMatch[] = data.hits.content.map(
          (content: ModelVersionsDataType, index: number): VehicleMatch => ({
            rank: index,
            item: {
              versionId: content.versionId.toString(),
              make: content.referential.make,
              model: content.referential.model,
              category: content.referential.category,
              energy: content.specs.energy,
              doors: content.specs.nbOfDoors,
              gearbox: content.specs.gearbox,
              trimLevel:
                content.specs.trimLevel === undefined || content.specs.trimLevel.length === 0
                  ? 'AUCUNE'
                  : content.specs.trimLevel,
              cubic: content.specs.cubic,
              fiscalHorsePower: content.specs.fiscalHorsePower,
              version: content.label,
            },
          }),
        );

        return versions;
      })
      .catch(r => {
        Sentry.captureException(r, {
          tags: {
            api: 'vehicle-referential',
            endpoint: `v2/makes/${make}/models/${model}/versions`,
            ...AxiosUtils.enrichTags(r),
          },
        });
        return Promise.reject({ message: SERVICE_DOWN });
      });
  }
}

export const VehicleReferentialService = new VehicleReferential();
